import { Box, Typography, FormControl, Select, MenuItem, Stack, Button, Chip } from "@mui/material";
import { FirstRowElementsComponent } from "./ClientListParts.jsx";
import { useResponsive } from "../../../utils/mobileresponsive.js";
import { StyledDatePicker, datePickerPopperSx } from "../../../components/ui/CalendarPopper.jsx";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const ALL_TAB = "All";

const TotalBadge = ({ label, value }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			backgroundColor: "#EEF4FB",
			border: "1px solid #c5d8ee",
			borderRadius: "8px",
			px: 1.5,
			py: 0.75,
			minWidth: 150,
		}}
	>
		<Typography
			sx={{
				fontSize: "0.65rem",
				fontWeight: 700,
				letterSpacing: 0.8,
				color: "#2E6DA4",
				textTransform: "uppercase",
				lineHeight: 1,
				mb: 0.4,
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: "1.1rem",
				fontWeight: 700,
				color: "#1D54A2",
				lineHeight: 1,
			}}
		>
			{value}
		</Typography>
	</Box>
);

export default function ClientListFilterControls({
	filters,
	setFilters,
	searchTerm,
	onSearchChange,
	applicantCount,
	policyCount,
	carrierOptions = [],
	statusOptions = [],
}) {
	const { isMobile } = useResponsive();

	const TAB_OPTIONS = [...statusOptions, ALL_TAB];
	const activeStatusTab = filters.policy_status || ALL_TAB;

	const handleStatusTab = (tab) => {
		setFilters((f) => ({
			...f,
			policy_status: tab === ALL_TAB ? "" : tab,
			offset: 0,
		}));
	};

	const handleCarrierChange = (e) => {
		setFilters((f) => ({
			...f,
			carrier: e.target.value,
			offset: 0,
		}));
	};

	const handleDateFrom = (val) => {
		setFilters((f) => ({
			...f,
			effective_date_from: val ? dayjs(val).format("YYYY-MM-DD") : "",
			offset: 0,
		}));
	};

	const handleDateTo = (val) => {
		setFilters((f) => ({
			...f,
			effective_date_to: val ? dayjs(val).format("YYYY-MM-DD") : "",
			offset: 0,
		}));
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box pl={1.5}>
				{/* ── Date Range (top right) ── */}
				{!isMobile && (
					<Stack direction="row" alignItems="center !important" justifyContent="flex-end" spacing={1} mb={2} pr={2}>
						<Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
							Effective Date:
						</Typography>
						<Stack direction="row" alignItems="center !important" spacing={1}>
							<Box sx={{ width: 178 }}>
								<StyledDatePicker
									label="From"
									value={filters.effective_date_from ? dayjs(filters.effective_date_from) : null}
									onChange={handleDateFrom}
									slotProps={{
										textField: { size: "small" },
										popper: { sx: datePickerPopperSx },
									}}
								/>
							</Box>
							<Typography color="text.secondary">—</Typography>
							<Box sx={{ width: 178 }}>
								<StyledDatePicker
									label="To"
									value={filters.effective_date_to ? dayjs(filters.effective_date_to) : null}
									onChange={handleDateTo}
									minDate={filters.effective_date_from ? dayjs(filters.effective_date_from) : undefined}
									slotProps={{
										textField: { size: "small" },
										popper: { sx: datePickerPopperSx },
									}}
								/>
							</Box>
						</Stack>
					</Stack>
				)}

				{/* ── Row 1: Totals / Carrier / Search ── */}
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "stretch", md: "center" }}
					justifyContent="space-between"
					spacing={{ xs: 2, md: 3 }}
					sx={{ px: 2, py: 2 }}
				>
					{/* Totals */}
					<Stack direction="row" alignItems="center !important" gap={1.5} width={"100%"}>
						<Typography variant="h6" letterSpacing={1} sx={{ color: "#1D54A2", fontWeight: 700, whiteSpace: "nowrap" }}>
							TOTALS
						</Typography>
						<Box
							sx={{
								width: "2px",
								height: "32px",
								backgroundColor: "#c5d8ee",
								borderRadius: "2px",
								mx: 0.5,
							}}
						/>
						<TotalBadge label="Applicant Count" value={applicantCount.toLocaleString()} />
						<TotalBadge label="Policy Count" value={policyCount.toLocaleString()} />
					</Stack>

					{isMobile && (
						<Stack spacing={1} mb={2}>
							<Typography fontWeight={600}>Effective Date:</Typography>
							<Stack direction="row" alignItems="center !important" spacing={1}>
								<StyledDatePicker
									label="From"
									value={filters.effective_date_from ? dayjs(filters.effective_date_from) : null}
									onChange={handleDateFrom}
									slotProps={{
										textField: { size: "small", fullWidth: true },
										popper: { sx: datePickerPopperSx },
									}}
								/>
								<Typography color="text.secondary" sx={{ flexShrink: 0 }}>
									—
								</Typography>
								<StyledDatePicker
									label="To"
									value={filters.effective_date_to ? dayjs(filters.effective_date_to) : null}
									onChange={handleDateTo}
									minDate={filters.effective_date_from ? dayjs(filters.effective_date_from) : undefined}
									slotProps={{
										textField: { size: "small", fullWidth: true },
										popper: { sx: datePickerPopperSx },
									}}
								/>
							</Stack>
						</Stack>
					)}

					{/* Carrier + Search */}
					<Stack
						direction={{ xs: "column", md: "row" }}
						alignItems={{ xs: "stretch", md: "center" }}
						spacing={2}
						sx={{ width: { xs: "100%", md: "auto" } }}
					>
						{/* Carrier Filter */}
						<Stack direction="row" alignItems="center !important" spacing={1} sx={{ width: "100%", minWidth: 200 }}>
							<Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
								Carrier:
							</Typography>
							<FormControl size="small" fullWidth>
								<Select
									value={filters.carrier || ""}
									onChange={handleCarrierChange}
									displayEmpty
									sx={{
										fontWeight: 600,
										"& .MuiSelect-select": { py: "6px" },
									}}
								>
									<MenuItem value="">All</MenuItem>
									{carrierOptions.map((c) => (
										<MenuItem key={c} value={c} sx={{ fontSize: 13 }}>
											{c}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Stack>

						{/* Search */}
						<Stack direction="row" alignItems="center !important" spacing={1} width={"100%"}>
							<Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
								Search:
							</Typography>
							<FirstRowElementsComponent searchTerm={searchTerm} onSearchChange={onSearchChange} />
						</Stack>
					</Stack>
				</Stack>

				{/* ── Row 2: Policy Status Tabs ── */}
				{isMobile ? (
					<Stack direction="row" alignItems="center !IMPORTANT" spacing={1.5} sx={{ px: 2 }}>
						<Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
							Status:
						</Typography>
						<FormControl size="small" fullWidth>
							<Select
								value={activeStatusTab}
								onChange={(e) => handleStatusTab(e.target.value)}
								sx={{
									fontWeight: 600,
									"& .MuiSelect-select": { py: "6px" },
								}}
							>
								{TAB_OPTIONS.map((tab) => (
									<MenuItem key={tab} value={tab} sx={{ fontSize: 13 }}>
										{tab}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				) : (
					<Box
						sx={{
							display: "flex",
							gap: 0.2,
							overflowX: "auto",
							mt: 4,
							pr: 0.3,
							justifyContent: "flex-end",
							"&::-webkit-scrollbar": { height: "4px" },
							"&::-webkit-scrollbar-thumb": {
								backgroundColor: "#3A7DC9",
								borderRadius: "4px",
							},
						}}
					>
						{TAB_OPTIONS.map((tab) => {
							const isActive = activeStatusTab === tab;
							return (
								<Button
									key={tab}
									onClick={() => handleStatusTab(tab)}
									disableElevation
									sx={{
										backgroundColor: !isActive ? "#FFFFFF !important" : "#1D54A2 !important",
										color: !isActive ? "#2E6DA4" : "#FFFFFF",
										textTransform: "none",
										fontWeight: "bold",
										borderBottomLeftRadius: 0,
										borderBottomRightRadius: 0,
										fontSize: "0.75rem",
										px: 2.5,
										py: "6px",
										whiteSpace: "nowrap",
										minWidth: "fit-content",
										border: !isActive ? "1px solid #c5d8ee" : "1px solid transparent",
										borderBottom: "none",
										"&:hover": {
											backgroundColor: !isActive ? "#f0f6ff" : "#1D54A2",
										},
										"&:focus": {
											backgroundColor: !isActive ? "#FFFFFF" : "#3A7DC9",
											color: !isActive ? "#2E6DA4" : "#FFFFFF",
										},
									}}
								>
									{tab}
								</Button>
							);
						})}
					</Box>
				)}
			</Box>
		</LocalizationProvider>
	);
}
